import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { Formik, useFormikContext } from 'formik';

import { bulkPDFJob, bulkPDFJobsMeta } from 'ducks';
import { duckify, filterDuck } from 'utils/duckify';
import Routes from '../../../../routes';
import Placeholder from '../../Placeholder';
import BulkUserJobModal from './BulkUserJobModal';
import './styles/BulkPrint.scss';

const ModalBody = ({
  pages,
  loadingComplete
}) => {
  const {
    values,
    errors,
    handleChange,
    setFieldValue
  } = useFormikContext();

  return (
    <Placeholder.loading ready={loadingComplete}>
      <Form>
        <Form.Group className='bulk-print-modal__form-group'>
          <Form.Label className='bulk-print-modal__label'>Download Options</Form.Label>
          <div className='bulk-print-modal__control'>
            {!values.include_uploaded_files && (
              <Form.Check
                id='single-file'
                type='radio'
                name='file_per_form'
                value='single'
                label='Download as a single PDF file'
                defaultChecked={values.file_per_form === 'single'}
                disabled={values.include_uploaded_files}
                onChange={handleChange}
              />
            )}
            <Form.Check
              id='separate-files'
              type='radio'
              name='file_per_form'
              value='zip'
              label='Download as ZIP file with a separate PDF for each form'
              defaultChecked={values.file_per_form === 'zip'}
              onChange={handleChange}
            />
            {!values.include_uploaded_files && (
              <Form.Check
                id='csv-format'
                type='radio'
                name='file_per_form'
                value='csv'
                label='Download as CSV'
                defaultChecked={values.file_per_form === 'csv'}
                disabled={values.include_uploaded_files}
                onChange={handleChange}
              />
            )}
          </div>
        </Form.Group>

        {values.file_per_form !== 'csv' && (<>
        <Form.Group className='bulk-print-modal__form-group'>
          <Form.Label className='bulk-print-modal__label'>Font Size</Form.Label>
          <div className='bulk-print-modal__control'>
            <Form.Check
              id='font-size-normal'
              type='radio'
              label='Standard'
              name='font_size'
              defaultChecked={values.font_size === 'normal'}
              value='normal'
              onChange={handleChange}
            />
            <Form.Check
              id='font-size-large'
              type='radio'
              label='Large'
              name='font_size'
              defaultChecked={values.font_size === 'large'}
              value='large'
              onChange={handleChange}
            />
          </div>
        </Form.Group>

        <Form.Group className='bulk-print-modal__form-group'>
          <Form.Label className='bulk-print-modal__label'>Pages to include</Form.Label>
          <div className='bulk-print-modal__control'>
            <Form.Check
              id='pages-all'
              type='radio'
              name='pages'
              label='All'
              value='all'
              defaultChecked={values.pages === 'all'}
              onChange={handleChange}
            />
            <Form.Check
              id='pages-select-check'
              type='radio'
              className='bulk-print-modal__pages-select-group form-check'
            >
              <Form.Check.Input
                className='bulk-print-modal__pages-select-radio'
                id='pages-select'
                type='radio'
                name='pages'
                value='selected'
                defaultChecked={values.pages === 'selected'}
                onChange={handleChange}
              />
              <Select
                className='bulk-print-modal__pages-select-select'
                disabled={values.pages !== 'selected'}
                multi
                options={pages.map(page => ({ label: page.name, value: page.id }))}
                onChange={selected => setFieldValue('selectedPages', selected)}
                value={values.selectedPages}
              />
            </Form.Check>
            {errors.pages && (
              <Form.Control.Feedback className='d-block' type='invalid'>
                Please select one or more pages.
              </Form.Control.Feedback>
            )}
          </div>
        </Form.Group>

        <Form.Group className='bulk-print-modal__form-group'>
          <Form.Label className='bulk-print-modal__label'>Form Options</Form.Label>
          <div className='bulk-print-modal__control'>
            <Form.Check
              id='page-numbers'
              type='checkbox'
              name='page_numbers'
              label='Include page numbers'
              defaultChecked={values.page_numbers}
              onChange={handleChange}
            />
            <Form.Check
              id='repeat-header'
              type='checkbox'
              name='repeat_header'
              label='Repeat header on every page'
              defaultChecked={values.repeat_header}
              onChange={handleChange}
            />
            <Form.Check
              id='break-per-page'
              type='checkbox'
              name='break_per_page'
              label='Include page breaks between each form page'
              defaultChecked={values.break_per_page}
              onChange={handleChange}
            />
            <Form.Check
              id='print-portrait'
              type='checkbox'
              name='print_portrait'
              label='Include contact portrait (if available)'
              defaultChecked={values.print_portrait}
              onChange={handleChange}
            />
            <Form.Check
              id='include-uploaded'
              type='checkbox'
              name='include_uploaded_files'
              label='Include uploaded files'
              defaultChecked={values.include_uploaded_files}
              onChange={handleChange}
            />
          </div>
        </Form.Group>
        </>)}
      </Form>
    </Placeholder.loading>
  );
};

ModalBody.propTypes = {
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ),
  loadingComplete: PropTypes.bool
};

ModalBody.defaultProps = {
  pages: [{
    id: '',
    name: ''
  }],
  loadingComplete: false
};

const BulkPrint = ({
  formType,
  contactIds,
  searchId,
  onClose,
  allDucksReady,
  bulkPrintPages,
  generatePDF,
  poll,
  cancel
}) => {
  const [job, setJob] = useState();
  const [downloadLink, setDownloadLink] = useState();

  const handleClose = () => (job && job.status === 'pending' ? cancel() : onClose());
  const submitJob = (values, setSubmitting) => {
    const csv = values.file_per_form === 'csv';
    const jobOptions = {
      ...values,
      file_per_form: (values.file_per_form === 'zip'),
      export_csv: csv,
      pages: (values.pages === 'all' ? [] : values.selectedPages.map(page => page.label))
    };
    delete jobOptions.selectedPages;

    setSubmitting(true);

    const submitPromise = new Promise((resolve, reject) => (
      generatePDF({ contact_ids: contactIds, form_type: formType, options: jobOptions, search_id: searchId })
        .then(jsonJob => {
          setJob(jsonJob);
          poll(jsonJob)
            .then(resp => {
              setSubmitting(false);
              setDownloadLink(Routes.api_user_jobs_job_attachment_path('bulk_pdf_job', resp.id));
              setJob(resp);
              resolve();
            })
            .catch(error => {
              setJob();
              if (error.status === 'cancelled') {
                onClose();
              } else {
                reject();
              }
            });
        })
        .catch(() => {
          setJob();
          reject();
        })
    ));

    return submitPromise;
  };

  return (
    <Formik
      initialValues={{
        break_per_page: true,
        file_per_form: 'single',
        font_size: 'normal',
        include_uploaded_files: false,
        page_numbers: true,
        pages: 'all',
        print_portrait: true,
        repeat_header: true,
        selectedPages: []
      }}
      validate={values => {
        const errors = {};
        if (values.pages !== 'all' && values.selectedPages.length === 0) {
          errors.pages = 'Please select one or more pages.';
        }

        return errors;
      }}
    >
      {({
        values,
        setSubmitting
      }) => (
        <BulkUserJobModal
          className='bulk-print-modal'
          initialBody={<ModalBody pages={bulkPrintPages} loadingComplete={allDucksReady} />}
          initialTitle='Print PDF Options'
          workingTitle='Print PDF'
          completedTitle='Download PDF File'
          downloadLink={downloadLink}
          onSubmit={() => submitJob(values, setSubmitting)}
          onClose={handleClose}
        />
      )}
    </Formik>
  );
};

BulkPrint.propTypes = {
  /** Form data to generate PDF of form */
  formType: PropTypes.shape({
    class: PropTypes.string,
    school_year_id: PropTypes.string
  }).isRequired,

  /** Array of Contact IDs to generate the selected forms for */
  contactIds: PropTypes.arrayOf(PropTypes.string).isRequired,

  /** Function to close and unmount this component */
  onClose: PropTypes.func.isRequired,

  /** allDucksReady */
  allDucksReady: PropTypes.bool.isRequired,

  /** Dispatches an action to start the PDF Generation job */
  generatePDF: PropTypes.func.isRequired,

  /** Dispatches an action to poll the status of the PDF Job */
  poll: PropTypes.func.isRequired,

  /** Cancells a poll action for cleanup */
  cancel: PropTypes.func.isRequired,

  /** bulkPrintPages Duck */
  bulkPrintPages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  })),

  /** Formik Values */
  values: PropTypes.shape({
    break_per_page: PropTypes.bool,
    file_per_form: PropTypes.string,
    font_size: PropTypes.string,
    include_uploaded_files: PropTypes.bool,
    page_numbers: PropTypes.bool,
    pages: PropTypes.string,
    print_portrait: PropTypes.bool,
    repeat_header: PropTypes.bool,
    selectedPages: PropTypes.arrayOf(PropTypes.string)
  }),

  /** Formik errors */
  errors: PropTypes.shape({
    pages: PropTypes.arrayOf(PropTypes.string)
  })
};

BulkPrint.defaultProps = {
  bulkPrintPages: [],
  values: {
    break_per_page: true,
    file_per_form: 'single',
    font_size: 'normal',
    include_uploaded_files: false,
    page_numbers: true,
    pages: 'all',
    print_portrait: true,
    repeat_header: true,
    selectedPages: []
  },
  errors: {}
};

const mapDucksToProps = props => ({
  bulkPrintPages: filterDuck(bulkPDFJobsMeta, { bulk_pdf_job: { form_type: props.formType } })
});

const mapDispatchToProps = (dispatch) => ({
  generatePDF: job => dispatch(bulkPDFJob.actions.create(job)).then(bulkPDFJob.actions.denormalizeObjects),
  poll: job => dispatch(bulkPDFJob.actions.poll(job)).then(bulkPDFJob.actions.denormalizeObjects),
  cancel: () => dispatch(bulkPDFJob.actions.cancelPoll())
});

export default duckify(mapDucksToProps, () => {}, mapDispatchToProps)(BulkPrint);
